import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAppointments } from "../redux/slices/appointmentSlice";
import AppointmentModal from "./AppointmentModal";
import "../styles/Calendar.css";

function CalendarView({ currentSection }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { appointments } = useSelector((state) => state.appointments);
  const doctorId = useSelector((state) => state.auth.doctor?.doctorId);
  const isAdmin = useSelector((state) => state.admin.isAuthenticated);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("day"); // "day" or "week"
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  useEffect(() => {
    // Fetch appointments for the current doctor (or all if admin)
    const idToPass = isAdmin ? null : doctorId;
    dispatch(fetchAppointments(idToPass));
  }, [dispatch, doctorId, isAdmin]);

  // Get all days in the current month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get the first day of the month
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Normalize appointment date to YYYY-MM-DD format
  const normalizeAppointmentDate = (dateStr) => {
    if (!dateStr) return "";
    // If it's a Date object, format it
    if (dateStr instanceof Date) {
      return formatDate(dateStr);
    }
    // If it's a string like "2026-03-16" or "2026-03-16T00:00:00.000Z", extract the date part
    if (typeof dateStr === "string") {
      return dateStr.split("T")[0];
    }
    return "";
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    const dateStr = formatDate(date);
    return appointments.filter((apt) => {
      const aptDate = normalizeAppointmentDate(apt.appointment_date);
      return aptDate === dateStr;
    });
  };

  // Calculate end time from start time and duration
  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
  };

  // Get week dates (Monday to Sunday)
  const getWeekDates = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    const startDate = new Date(d.setDate(diff));
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      weekDates.push(new Date(date));
    }
    return weekDates;
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const previousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  // Render Month View
  const renderMonthView = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dateStr = formatDate(date);
      const dayAppointments = getAppointmentsForDate(date);
      const isSelected = formatDate(selectedDate) === dateStr;
      const isToday = formatDate(new Date()) === dateStr;

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? "selected" : ""} ${isToday ? "today" : ""}`}
          onClick={() => setSelectedDate(date)}
        >
          <div className="day-number">{day}</div>
          <div className="day-appointments">
            {dayAppointments.length > 0 && (
              <div className="appointment-indicator" title={`${dayAppointments.length} appointment${dayAppointments.length !== 1 ? 's' : ''}`}></div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // Render Week View
  const renderWeekView = () => {
    const weekDates = getWeekDates(selectedDate);
    const hours = Array.from({ length: 16 }, (_, i) => i + 8); // 08:00 to 23:00

    return (
      <div className="week-view">
        <div className="week-header">
          {weekDates.map((date, idx) => (
            <div key={idx} className="week-day-header">
              <div className="week-day-name">
                {date.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
              <div className={`week-day-date ${formatDate(date) === formatDate(new Date()) ? "today" : ""}`}>
                {date.getDate()}
              </div>
            </div>
          ))}
        </div>

        <div className="week-grid">
          {hours.map((hour) => (
            <div key={`hour-${hour}`} className="week-hour-row">
              <div className="hour-label">
                {String(hour).padStart(2, "0")}:00
              </div>
              {weekDates.map((date, dayIdx) => {
                const dayAppointments = getAppointmentsForDate(date);
                const hourAppointments = dayAppointments.filter((apt) => {
                  const [aptHour] = apt.appointment_time.split(":");
                  return parseInt(aptHour) === hour;
                });

                return (
                  <div key={`${dayIdx}-${hour}`} className="week-hour-cell">
                    {hourAppointments.map((apt, idx) => (
                      <div 
                        key={idx} 
                        className="week-appointment"
                        onClick={() => navigate(`/consultation/${apt.appointment_id}`, { state: { appointment: apt, activeSection: currentSection } })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="appointment-time">
                          {apt.appointment_time} - {calculateEndTime(apt.appointment_time, apt.appointment_duration)}
                        </div>
                        <div className="appointment-patient">
                          {apt.patient_first_name} {apt.patient_last_name}
                        </div>
                        <div className="appointment-room">{apt.room_number}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render Day View Details
  const dayAppointments = getAppointmentsForDate(selectedDate);

  return (
    <div className="calendar-container">
      <h2>📅 Appointment Calendar</h2>

      {/* Controls */}
      <div className="calendar-controls">
        <button onClick={goToToday} className="today-btn">
          Today
        </button>

        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === "day" ? "active" : ""}`}
            onClick={() => setViewMode("day")}
          >
            Day View
          </button>
          <button
            className={`toggle-btn ${viewMode === "week" ? "active" : ""}`}
            onClick={() => setViewMode("week")}
          >
            Week View
          </button>
        </div>

        {viewMode === "day" ? (
          <div className="month-nav">
            <button onClick={previousMonth} title="Previous month">
              <svg className="nav-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <span>
              {currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </span>
            <button onClick={nextMonth} title="Next month">
              <svg className="nav-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        ) : (
          <div className="week-nav">
            <button onClick={previousWeek} title="Previous week">
              <svg className="nav-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              <span>Week</span>
            </button>
            <span>
              Week of {selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
            <button onClick={nextWeek} title="Next week">
              <span>Week</span>
              <svg className="nav-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Calendar View */}
      <div className="calendar-content">
        {viewMode === "day" ? (
          <div className="day-view">
            {/* Month Calendar */}
            <div className="month-calendar">
              <div className="calendar-header">
                <span>Sun</span>
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
              </div>
              <div className="calendar-grid">{renderMonthView()}</div>
            </div>

            {/* Day Details */}
            <div className="day-details">
              <div className="day-details-header">
                <h3>
                  {selectedDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </h3>
                <button
                  className="schedule-appointment-btn"
                  onClick={() => setIsScheduleModalOpen(true)}
                >
                  + Schedule Appointment
                </button>
              </div>

              {dayAppointments.length === 0 ? (
                <p className="no-appointments">No appointments scheduled</p>
              ) : (
                <div className="appointments-list">
                  {dayAppointments.map((apt, idx) => (
                    <div 
                      key={idx} 
                      className="appointment-card"
                      onClick={() => navigate(`/consultation/${apt.appointment_id}`, { state: { appointment: apt, activeSection: currentSection } })}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="appointment-header">
                        <span className="appointment-time">
                          {apt.appointment_time} - {calculateEndTime(apt.appointment_time, apt.appointment_duration)}
                        </span>
                        <span className={`status-badge ${apt.status}`}>{apt.status}</span>
                      </div>
                      <div className="appointment-details">
                        <div>
                          <strong>Patient:</strong> {apt.patient_first_name} {apt.patient_last_name}
                        </div>
                        <div>
                          <strong>Room:</strong> {apt.room_number}
                        </div>
                        <div>
                          <strong>Time:</strong> {apt.appointment_time.substring(0, 5)} - {calculateEndTime(apt.appointment_time, apt.appointment_duration).substring(0, 5)}
                        </div>
                        <div>
                          <strong>Duration:</strong> {apt.appointment_duration} minutes
                        </div>
                        {apt.remarks && (
                          <div className="appointment-remarks-section">
                            <strong>Remarks:</strong>
                            <div className="appointment-remarks-text" title={apt.remarks}>
                              {apt.remarks.substring(0, 80)}{apt.remarks.length > 80 ? '...' : ''}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          renderWeekView()
        )}
      </div>

      {/* Schedule Appointment Modal */}
      <AppointmentModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        preSelectedDate={selectedDate}
        isScheduleMode={true}
      />
    </div>
  );
}

export default CalendarView;
